import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Container,
} from "reactstrap";
import {
  DEFAULT_PAGE_SIZE,
  resolveMetadataTableColumnsFromApiResponse,
  urlEndPoint,
} from "helpers/GeneralHelper";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import CandidateListFilterPrompt from "components/Common/CandidateListFilterPrompt";
import {
  fetchRtrAcknowledgeMetadata,
  submitRtrAcknowledgeFilterData,
  submitRtrAcknowledgeAction,
  submitRtrAcknowledgeNotes,
  deleteRtrAcknowledge,
} from "model/CallModel";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  countActiveMetadataFilters,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const DEFAULT_ACKNOWLEDGE_TYPE = "pending";
const RTR_ACKNOWLEDGE_PREFILL_STORAGE_KEY = "rtrAcknowledgePrefillFilters";

const readStoredRtrAcknowledgePrefillFilters = () => {
  try {
    const raw = sessionStorage.getItem(RTR_ACKNOWLEDGE_PREFILL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const clearStoredRtrAcknowledgePrefillFilters = () => {
  try {
    sessionStorage.removeItem(RTR_ACKNOWLEDGE_PREFILL_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
};

const buildPrefillFromSearchParams = (searchParams) => {
  const search = String(searchParams?.get?.("search") ?? "").trim();
  const acknowledgeType = String(
    searchParams?.get?.("acknowledgeType") ??
      searchParams?.get?.("decision") ??
      ""
  ).trim();
  if (!search && !acknowledgeType) return null;
  return {
    ...(search ? { search } : {}),
    ...(acknowledgeType ? { acknowledgeType, decision: acknowledgeType } : {}),
  };
};

const resolveInitialRtrAcknowledgePrefill = (location, searchParams) => {
  const fromQuery = buildPrefillFromSearchParams(searchParams);
  if (fromQuery) return fromQuery;

  const fromNavigation = location?.state?.prefillFilters;
  if (fromNavigation && typeof fromNavigation === "object") {
    return fromNavigation;
  }

  const fromStorage = readStoredRtrAcknowledgePrefillFilters();
  if (fromStorage) {
    clearStoredRtrAcknowledgePrefillFilters();
  }
  return fromStorage;
};

const resolveFilterDefinitionByFieldOrApi = (
  metadataFilters = [],
  fieldNames = [],
  apiKeys = []
) => {
  const normalizedFields = fieldNames.map((fieldName) =>
    String(fieldName ?? "").trim().toLowerCase()
  );
  const normalizedApis = apiKeys.map((apiKey) =>
    String(apiKey ?? "").trim().toLowerCase()
  );
  return (Array.isArray(metadataFilters) ? metadataFilters : []).find(
    (definitionRow) => {
      const field = String(definitionRow?.field ?? "").trim().toLowerCase();
      const api = String(definitionRow?.api ?? "").trim().toLowerCase();
      return normalizedFields.includes(field) || normalizedApis.includes(api);
    }
  );
};

const applyPrefillDropdownFilterValue = (
  nextFilter,
  metadataFilters,
  { fieldNames, apiKeys, value }
) => {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) return;
  const definition = resolveFilterDefinitionByFieldOrApi(
    metadataFilters,
    fieldNames,
    apiKeys
  );
  const fieldKey = definition?.field || fieldNames[0];
  if (!fieldKey) return;
  nextFilter[fieldKey] = trimmedValue;
};

const hasPendingRtrAcknowledgePrefill = (prefill = {}) =>
  Boolean(
    String(prefill?.search ?? "").trim() ||
      String(prefill?.acknowledgeType ?? prefill?.decision ?? "").trim()
  );

const buildRtrAcknowledgePrefillFilterState = (
  prefill = {},
  emptyFilter = {},
  metadataFilters = []
) => {
  const nextFilter = { ...emptyFilter };
  const search = String(prefill?.search ?? "").trim();
  if (search) {
    nextFilter.search = search;
  }

  applyPrefillDropdownFilterValue(nextFilter, metadataFilters, {
    fieldNames: ["acknowledgeType", "decision"],
    apiKeys: ["get_rtrdecision_options"],
    value: prefill?.acknowledgeType ?? prefill?.decision,
  });

  const acknowledgeType = String(
    prefill?.acknowledgeType ?? prefill?.decision ?? ""
  ).trim();
  if (acknowledgeType) {
    nextFilter.acknowledgeType = acknowledgeType;
  }

  return nextFilter;
};

const normalizeOptionalStringFilterValueOrNull = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const trimmed = String(rawValue).trim();
  return trimmed === "" ? null : trimmed;
};

const resolveAcknowledgeTypeForPayload = (rawValue) =>
  normalizeOptionalStringFilterValueOrNull(rawValue);

const normalizeRtrAcknowledgeFilterPayload = (
  rawFilterBody = {},
  metadataFiltersFromApi = []
) => {
  const outboundPayload = { ...rawFilterBody, checkAccess: true };

  const dynamicNormalizeKeysFromMetadata = (
    Array.isArray(metadataFiltersFromApi) ? metadataFiltersFromApi : []
  ).reduce((uniqueKeyAccumulator, filterDefinitionRow) => {
    const backendFieldIdentifier = filterDefinitionRow?.field;
    if (
      backendFieldIdentifier &&
      backendFieldIdentifier !== "search" &&
      !uniqueKeyAccumulator.includes(backendFieldIdentifier)
    ) {
      return [...uniqueKeyAccumulator, backendFieldIdentifier];
    }
    return uniqueKeyAccumulator;
  }, []);

  dynamicNormalizeKeysFromMetadata.forEach((keywordKeyFromPayload) => {
    if (keywordKeyFromPayload in outboundPayload) {
      outboundPayload[keywordKeyFromPayload] = normalizeOptionalStringFilterValueOrNull(
        outboundPayload[keywordKeyFromPayload]
      );
    }
  });

  outboundPayload.acknowledgeType = resolveAcknowledgeTypeForPayload(
    outboundPayload.acknowledgeType
  );

  if (!outboundPayload.fromDate && outboundPayload.fromDate !== 0) {
    outboundPayload.fromDate = null;
  } else if (outboundPayload.fromDate === "") {
    outboundPayload.fromDate = null;
  }
  if (!outboundPayload.toDate && outboundPayload.toDate !== 0) {
    outboundPayload.toDate = null;
  } else if (outboundPayload.toDate === "") {
    outboundPayload.toDate = null;
  }

  Object.keys(outboundPayload).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete outboundPayload[payloadKey];
    }
  });

  return outboundPayload;
};

const RTR_ACKNOWLEDGE_METADATA_COLUMN_OPTIONS = {
  resolveAccessor: (col = {}) => String(col?.field ?? "").trim(),
};

/** Metadata GET: `{ data: { filters, table: { columns } } }`. */
const resolveRtrAcknowledgeMetadataFromResponse = (metadataResponse = {}) => {
  const meta =
    metadataResponse?.data != null && typeof metadataResponse.data === "object"
      ? metadataResponse.data
      : metadataResponse;
  const normalizedMeta = meta && typeof meta === "object" ? meta : {};

  return {
    filters: Array.isArray(normalizedMeta?.filters) ? normalizedMeta.filters : [],
    tableColumns: resolveMetadataTableColumnsFromApiResponse(
      metadataResponse,
      RTR_ACKNOWLEDGE_METADATA_COLUMN_OPTIONS
    ),
  };
};

/** Same envelope shape as all-candidates: `data.records` + pagination on `data`. */
const parseRtrAcknowledgeFilterResponse = (response = {}) => {
  const responseData = response?.data ?? response ?? {};
  const listData =
    responseData?.records ??
    responseData?.data ??
    (Array.isArray(responseData) ? responseData : []);
  const records = Array.isArray(listData) ? listData : [];
  return { responseData, records };
};

const mapRecordsToRows = (list = [], tableColumns = []) => {
  const metadataFieldKeys = (Array.isArray(tableColumns) ? tableColumns : [])
    .map((columnDefinition) => String(columnDefinition?.accessor ?? "").trim())
    .filter(Boolean);

  return (Array.isArray(list) ? list : []).map((item = {}) => {
    const row = {
      ...item,
      id: item?.id || item?.candidateId || item?.candidateClientId || "",
    };

    metadataFieldKeys.forEach((fieldKey) => {
      const snakeCaseKey = fieldKey.replace(/([A-Z])/g, "_$1").toLowerCase();
      const rawValue = item?.[fieldKey] ?? item?.[snakeCaseKey];
      if (rawValue !== undefined) {
        row[fieldKey] = rawValue;
      }
    });

    if (metadataFieldKeys.includes("notes")) {
      row.notes = String(row?.notes ?? row?.note ?? "").trim();
    }

    return row;
  });
};

const resolveRowNotes = (row = {}) => String(row?.notes ?? row?.note ?? "").trim();

const resolveRowId = (row = {}) =>
  String(row?.id ?? row?._id ?? row?.candidateClientId ?? row?.candidateId ?? "").trim();

const extractRtrAcknowledgePatchFromApiResponse = (response = {}) => {
  const responseData = response?.data;
  if (!responseData || typeof responseData !== "object") return {};
  if (Array.isArray(responseData)) {
    const firstRecord = responseData[0];
    return firstRecord && typeof firstRecord === "object" ? firstRecord : {};
  }
  if (responseData.record && typeof responseData.record === "object") {
    return responseData.record;
  }
  if (Array.isArray(responseData.records) && responseData.records[0]) {
    return responseData.records[0];
  }
  return responseData;
};

const buildRtrAcknowledgeDecisionDisplay = (acknowledgeType = "") => {
  const normalized = String(acknowledgeType ?? "").trim().toLowerCase();
  if (normalized === "yes") return "Yes";
  if (normalized === "no") return "No";
  return "";
};

const shouldRemoveRtrAcknowledgeRowAfterAction = (
  submittedAcknowledgeType = "",
  activeAcknowledgeTypeFilter = ""
) => {
  const normalizedSubmittedType = String(submittedAcknowledgeType ?? "").trim().toLowerCase();
  const normalizedActiveFilter = String(activeAcknowledgeTypeFilter ?? "").trim().toLowerCase();
  if (!normalizedActiveFilter || normalizedActiveFilter === "pending") return true;
  return normalizedActiveFilter !== normalizedSubmittedType;
};

const RTRAcknowledge = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();
  const { permissionData } = usePermissionContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("RtrAcknowledge");
  const initialFilterFetchedRef = useRef(false);
  const metadataLoadedRef = useRef(false);
  const prefillHandledRef = useRef(false);
  const prefillInitializedRef = useRef(false);
  const pendingPrefillRef = useRef(null);

  if (!prefillInitializedRef.current) {
    prefillInitializedRef.current = true;
    pendingPrefillRef.current = resolveInitialRtrAcknowledgePrefill(
      location,
      searchParams
    );
  }

  const rtrAcknowledgeActionPermissions = useMemo(
    () => ({
      canAcknowledge: Boolean(permissionData?.CallRTRAcknowledgeAcknowledgeAccess),
      canChangeNotes: Boolean(permissionData?.CallRTRAcknowledgeNoteChangeAccess),
      canDelete: Boolean(permissionData?.CallRTRAcknowledgeDeleteAccess),
    }),
    [permissionData]
  );

  const [listFilter, setListFilter] = useState({
    acknowledgeType: DEFAULT_ACKNOWLEDGE_TYPE,
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterApplied, setFilterApplied] = useState(true);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [toolbarFilterRemountKey, setToolbarFilterRemountKey] = useState(0);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    acknowledgeType: DEFAULT_ACKNOWLEDGE_TYPE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [filterMetaData, setFilterMetaData] = useState([]);
  const [toolbarDropdownReactSelectOptionsByFieldName, setToolbarDropdownReactSelectOptionsByFieldName] =
    useState({});
  const rowNotesRef = useRef({});
  const lastSavedNotesRef = useRef({});

  const metadataToolbarFilterDefinitions = useMemo(
    () =>
      (Array.isArray(filterMetaData) ? filterMetaData : []).filter(
        (filterDefinitionFromMetadataApi) =>
          filterDefinitionFromMetadataApi?.field &&
          filterDefinitionFromMetadataApi?.isDefault === true
      ),
    [filterMetaData]
  );

  const metadataModalOnlyFilterDefinitions = useMemo(
    () =>
      (Array.isArray(filterMetaData) ? filterMetaData : []).filter(
        (filterDefinitionFromMetadataApi) =>
          filterDefinitionFromMetadataApi?.field &&
          filterDefinitionFromMetadataApi?.isDefault !== true
      ),
    [filterMetaData]
  );

  const usesMetadataDrivenFilterUi =
    Array.isArray(filterMetaData) && filterMetaData.length > 0;

  const shouldShowTableHeaderFilterAndResetControls = useMemo(
    () =>
      !usesMetadataDrivenFilterUi || metadataModalOnlyFilterDefinitions.length > 0,
    [usesMetadataDrivenFilterUi, metadataModalOnlyFilterDefinitions]
  );

  const activeModalFilterCount = useMemo(
    () => countActiveMetadataFilters(listFilter, metadataModalOnlyFilterDefinitions),
    [listFilter, metadataModalOnlyFilterDefinitions]
  );

  const shouldRenderMetadataToolbarFilterRow = metadataToolbarFilterDefinitions.length > 0;

  const buildBaselineEmptyFilterState = useCallback(() => {
    const baselineFilterState = {};
    (Array.isArray(filterMetaData) ? filterMetaData : []).forEach(
      (filterDefinitionFromMetadataApi) => {
        const filterFieldKey = filterDefinitionFromMetadataApi?.field;
        if (!filterFieldKey) return;
        baselineFilterState[filterFieldKey] =
          filterDefinitionFromMetadataApi?.type === "date" ? null : "";
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          baselineFilterState[getAsyncFilterLabelFieldKey(filterFieldKey)] = "";
        }
      }
    );
    if (!("acknowledgeType" in baselineFilterState)) {
      baselineFilterState.acknowledgeType = "";
    }
    return baselineFilterState;
  }, [filterMetaData]);

  const filterModalData = useMemo(() => {
    if (metadataModalOnlyFilterDefinitions.length) {
      return mapFilterDefinitionsToModalData(
        metadataModalOnlyFilterDefinitions,
        listFilter
      );
    }
    if (usesMetadataDrivenFilterUi) return [];
    return [];
  }, [metadataModalOnlyFilterDefinitions, listFilter, usesMetadataDrivenFilterUi]);

  useEffect(() => {
    const toolbarDropdownDefinitions = metadataToolbarFilterDefinitions.filter(
      (filterDefinitionFromMetadataApi) =>
        filterDefinitionFromMetadataApi?.type === "dropdown" &&
        filterDefinitionFromMetadataApi?.field
    );
    if (!toolbarDropdownDefinitions.length) {
      setToolbarDropdownReactSelectOptionsByFieldName({});
      return;
    }
    let requestCancelled = false;
    (async () => {
      const optionTuples = await Promise.all(
        toolbarDropdownDefinitions.map(async (filterDefinitionFromMetadataApi) => {
          const dropdownFieldBackendKey = filterDefinitionFromMetadataApi.field;
          const staticOptions = Array.isArray(filterDefinitionFromMetadataApi?.itemArray)
            ? filterDefinitionFromMetadataApi.itemArray
            : [];
          if (staticOptions.length) return [dropdownFieldBackendKey, staticOptions];
          const fetchedOptions = await getDropdownOptionsByApiKey(
            filterDefinitionFromMetadataApi?.api
          );
          return [dropdownFieldBackendKey, fetchedOptions];
        })
      );
      if (requestCancelled) return;
      const optionsByFieldName = {};
      optionTuples.forEach(([fieldKey, options]) => {
        optionsByFieldName[fieldKey] = options;
      });
      setToolbarDropdownReactSelectOptionsByFieldName(optionsByFieldName);
    })();
    return () => {
      requestCancelled = true;
    };
  }, [metadataToolbarFilterDefinitions, getDropdownOptionsByApiKey]);

  useEffect(() => {
    if (!Array.isArray(filterMetaData) || !filterMetaData.length) return;
    setListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      filterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedFilterState)) {
          mergedFilterState[backendFilterFieldIdentifier] =
            filterDefinitionFromMetadataApi?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(backendFilterFieldIdentifier);
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [filterMetaData]);

  const buildPayloadFromFilterForm = (form = {}, overrides = {}) => {
    const searchKeywordTrimmed = String(form?.search || "").trim();
    const fromDateForFilter = normalizeMetadataFilterDateForApi(form?.fromDate);
    const toDateForFilter = normalizeMetadataFilterDateForApi(form?.toDate);

    const looselyMappedMetadataFilterValuesFromUiFormLayer = {};
    if (Array.isArray(filterMetaData)) {
      filterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier || backendFilterFieldIdentifier === "search") return;
        looselyMappedMetadataFilterValuesFromUiFormLayer[backendFilterFieldIdentifier] =
          form?.[backendFilterFieldIdentifier];
      });
    }

    return normalizeRtrAcknowledgeFilterPayload(
      {
        ...looselyMappedMetadataFilterValuesFromUiFormLayer,
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        nameContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        emailContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        fromDate: fromDateForFilter,
        toDate: toDateForFilter,
        acknowledgeType: resolveAcknowledgeTypeForPayload(
          form?.acknowledgeType ?? looselyMappedMetadataFilterValuesFromUiFormLayer?.acknowledgeType
        ),
        ...overrides,
      },
      filterMetaData
    );
  };

  const getRtrAcknowledgeList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false } = options;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            // checkAccess: true,
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
          }
        : normalizeRtrAcknowledgeFilterPayload(
            {
              ...requestPayload,
              ...payloadOverrides,
            },
            filterMetaData
          );

      const response = await submitRtrAcknowledgeFilterData(payload);
      const { responseData, records } = parseRtrAcknowledgeFilterResponse(response);

      const mappedRows = mapRecordsToRows(records, tableMetaData);
      setTableData(mappedRows);
      const notesByRowId = mappedRows.reduce((accumulator, row) => {
        const rowId = resolveRowId(row);
        if (!rowId) return accumulator;
        return { ...accumulator, [rowId]: resolveRowNotes(row) };
      }, {});
      rowNotesRef.current = notesByRowId;
      lastSavedNotesRef.current = { ...notesByRowId };

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          const clearedServerFilterSnapshotAfterPaginationReset =
            normalizeRtrAcknowledgeFilterPayload(
              {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                ...Object.fromEntries(
                  (Array.isArray(filterMetaData) ? filterMetaData : [])
                    .filter(
                      (filterDefinitionFromMetadataApi) =>
                        filterDefinitionFromMetadataApi?.field &&
                        filterDefinitionFromMetadataApi.field !== "search"
                    )
                    .map((filterDefinitionFromMetadataApi) => [
                      filterDefinitionFromMetadataApi.field,
                      null,
                    ])
                ),
              },
              filterMetaData
            );
          setRequestPayload(clearedServerFilterSnapshotAfterPaginationReset);
          setListFilter(buildBaselineEmptyFilterState());
        } else {
          setRequestPayload(normalizeRtrAcknowledgeFilterPayload(payload, filterMetaData));
        }
      } else {
        setTableData([]);
        rowNotesRef.current = {};
        lastSavedNotesRef.current = {};
        if (response) {
          toast.error(response?.message || "Unable to load RTR acknowledgement records");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load RTR acknowledgement records");
      setTableData([]);
      rowNotesRef.current = {};
      lastSavedNotesRef.current = {};
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadMetadata = async () => {
      if (metadataLoadedRef.current) return;
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema(
          "metadata:rtrAcknowledge",
          () => fetchRtrAcknowledgeMetadata()
        );
        const { filters, tableColumns } = resolveRtrAcknowledgeMetadataFromResponse(metaRes);
        setTableMetaData(tableColumns);
        setFilterMetaData(filters);
      } catch (e) {
        toast.error(e?.message || "Unable to load RTR acknowledgement metadata");
        setTableMetaData([]);
        setFilterMetaData([]);
      } finally {
        metadataLoadedRef.current = true;
      }
    };
    loadMetadata();
  }, [fetchMetadataSchema]);

  useEffect(() => {
    if (!settingsReady || !metadataLoadedRef.current || initialFilterFetchedRef.current) return;
    if (hasPendingRtrAcknowledgePrefill(pendingPrefillRef.current)) return;
    initialFilterFetchedRef.current = true;
    setFilterApplied(true);
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setRequestPayload((prev) => ({ ...prev, pageSize }));

    const loadInitialList = async () => {
      setLoading(true);
      try {
        const payload = normalizeRtrAcknowledgeFilterPayload(
          {
            pageNumber: 1,
            pageSize,
            acknowledgeType: DEFAULT_ACKNOWLEDGE_TYPE,
          },
          filterMetaData
        );
        const response = await submitRtrAcknowledgeFilterData(payload);
        const { responseData, records } = parseRtrAcknowledgeFilterResponse(response);

        const mappedRows = mapRecordsToRows(records, tableMetaData);
        setTableData(mappedRows);
        const notesByRowId = mappedRows.reduce((accumulator, row) => {
          const rowId = resolveRowId(row);
          if (!rowId) return accumulator;
          return { ...accumulator, [rowId]: resolveRowNotes(row) };
        }, {});
        rowNotesRef.current = notesByRowId;
        lastSavedNotesRef.current = { ...notesByRowId };

        if (response == null || response?.success !== false) {
          setPaginationMeta({
            pageNumber: Number(responseData?.pageNumber) || 1,
            pageSize: Number(responseData?.pageSize) || pageSize,
            totalPages: Number(responseData?.totalPages) || 0,
            totalRecords: Number(responseData?.totalRecords) || records.length,
          });
          setRequestPayload(normalizeRtrAcknowledgeFilterPayload(payload, filterMetaData));
        } else {
          setTableData([]);
          rowNotesRef.current = {};
          lastSavedNotesRef.current = {};
          if (response) {
            toast.error(response?.message || "Unable to load RTR acknowledgement records");
          }
        }
      } catch (err) {
        toast.error(err?.message || "Unable to load RTR acknowledgement records");
        setTableData([]);
        rowNotesRef.current = {};
        lastSavedNotesRef.current = {};
      } finally {
        setLoading(false);
      }
    };
    loadInitialList();
  }, [settingsReady, settingsPageSize, filterMetaData, tableMetaData]);

  useEffect(() => {
    if (!settingsReady || prefillHandledRef.current) return;
    const prefill = pendingPrefillRef.current;
    if (!hasPendingRtrAcknowledgePrefill(prefill)) return;
    if (!Array.isArray(filterMetaData) || !filterMetaData.length) return;

    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;

    prefillHandledRef.current = true;
    initialFilterFetchedRef.current = true;
    const emptyFilter = buildBaselineEmptyFilterState();
    const mergedFilter = buildRtrAcknowledgePrefillFilterState(
      prefill,
      emptyFilter,
      filterMetaData
    );

    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setListFilter(mergedFilter);
    setFilterApplied(true);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);

    const applyPrefill = async () => {
      const nextServerFilterPayload = buildPayloadFromFilterForm(mergedFilter, {
        pageNumber: 1,
        pageSize,
      });
      await getRtrAcknowledgeList(nextServerFilterPayload);
      clearStoredRtrAcknowledgePrefillFilters();
      pendingPrefillRef.current = null;
      if (location.search) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };
    applyPrefill();
  }, [
    buildBaselineEmptyFilterState,
    filterMetaData,
    location.pathname,
    location.search,
    navigate,
    settingsReady,
    settingsPageSize,
  ]);

  const handleFilterSubmit = async (filterFormValuesFromModal = {}) => {
    const mergedListFilter = mergeModalSubmitIntoListFilter(
      listFilter,
      filterModalData,
      filterFormValuesFromModal
    );
    setListFilter(mergedListFilter);
    setCommonFilterModalOpen(false);
    setFilterApplied(true);
    const nextServerFilterPayload = buildPayloadFromFilterForm(mergedListFilter);
    await getRtrAcknowledgeList(nextServerFilterPayload);
  };

  const handleFilterReset = async () => {
    const resetFilterState = buildBaselineEmptyFilterState();
    setListFilter(resetFilterState);
    setCommonFilterModalOpen(false);
    setFilterApplied(true);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);
    const nextServerFilterPayload = buildPayloadFromFilterForm(resetFilterState, {
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      acknowledgeType: null,
    });
    await getRtrAcknowledgeList(nextServerFilterPayload);
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter]);

  const handleUpperToolbarFilterSearch = async () => {
    setFilterApplied(true);
    const nextServerFilterPayload = buildPayloadFromFilterForm(listFilter);
    await getRtrAcknowledgeList(nextServerFilterPayload);
  };

  const handleToolbarTextFilterInputChange = (backendFilterFieldKey, nextInputStringValue) => {
    setListFilter((previousFilterState) => ({
      ...previousFilterState,
      [backendFilterFieldKey]: nextInputStringValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (backendFilterFieldKey, nextSelectedOption) => {
    setListFilter((previousFilterState) => ({
      ...previousFilterState,
      [backendFilterFieldKey]: nextSelectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFilterFieldKey)]:
        nextSelectedOption?.label ?? "",
    }));
  };

  const handlePageChange = async (page) => {
    if (!filterApplied) return;
    await getRtrAcknowledgeList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    if (!filterApplied) return;
    await getRtrAcknowledgeList({ pageNumber: 1, pageSize: rows });
  };

  const handleRowNotesChange = useCallback((row = {}, notesValue = "") => {
    const rowId = resolveRowId(row);
    if (!rowId) return;

    const nextNotes = String(notesValue ?? "");
    rowNotesRef.current[rowId] = nextNotes;
    setTableData((previousRows) =>
      previousRows.map((item) =>
        resolveRowId(item) === rowId ? { ...item, notes: nextNotes } : item
      )
    );
  }, []);

  const handleRowNotesBlur = useCallback(async (row = {}, notesValue = "") => {
    const rowId = resolveRowId(row);
    if (!rowId) return;

    const nextNotes = String(notesValue ?? "");
    const previousSavedNotes = String(lastSavedNotesRef.current[rowId] ?? "");
    if (nextNotes === previousSavedNotes) return;

    rowNotesRef.current[rowId] = nextNotes;

    try {
      const response = await submitRtrAcknowledgeNotes(rowId, { notes: nextNotes });
      if(response?.success){
        toast.success(response?.message);
        return
      }
      if (response == null || response?.success === false) {
        toast.error(response?.message || "Unable to save notes");
        return;
      }
      lastSavedNotesRef.current[rowId] = nextNotes;
    } catch (error) {
      toast.error(error?.message || "Unable to save notes");
    }
  }, []);

  const applyRtrAcknowledgeRowPatch = useCallback((recordId, partialRawUpdate = {}) => {
    const targetId = String(recordId ?? "").trim();
    if (!targetId || !partialRawUpdate || typeof partialRawUpdate !== "object") return;

    setTableData((previousRows) => {
      let rowMatched = false;
      const nextRows = previousRows.map((tableRow) => {
        if (resolveRowId(tableRow) !== targetId) return tableRow;
        rowMatched = true;
        const mergedRawRecord = {
          ...tableRow,
          ...partialRawUpdate,
          id: partialRawUpdate?.id ?? tableRow?.id ?? targetId,
        };
        const [mappedTableRow] = mapRecordsToRows([mergedRawRecord], tableMetaData);
        return mappedTableRow;
      });
      return rowMatched ? nextRows : previousRows;
    });
  }, [tableMetaData]);

  const removeRtrAcknowledgeRowFromList = useCallback((recordId = "") => {
    const targetId = String(recordId ?? "").trim();
    if (!targetId) return;

    setTableData((previousRows) => {
      const nextRows = previousRows.filter(
        (tableRow) => resolveRowId(tableRow) !== targetId
      );
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount > 0) {
        setPaginationMeta((previousPagination) => ({
          ...previousPagination,
          totalRecords: Math.max(
            0,
            (Number(previousPagination?.totalRecords) || 0) - removedCount
          ),
        }));
      }
      return nextRows;
    });
    delete rowNotesRef.current[targetId];
    delete lastSavedNotesRef.current[targetId];
  }, []);

  const submitAcknowledgeAction = useCallback(
    async (row = {}, acknowledgeType = "") => {
      const normalizedType = String(acknowledgeType ?? "").trim().toLowerCase();
      if (normalizedType !== "yes" && normalizedType !== "no") return;

      const isYes = normalizedType === "yes";
      const swalResult = await Swal.fire({
        title: isYes ? "Yes" : "No",
        text: isYes
          ? "Are you sure you want to acknowledge for this record?"
          : "Are you sure you dont want to acknowledge for this record?",
        icon: isYes ? "question" : "warning",
        showCancelButton: true,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (!swalResult?.isConfirmed) return;

      const rowId = resolveRowId(row);
      if (!rowId) {
        toast.error("Record ID not found");
        return;
      }

      try {
        const canChangeNotes = rtrAcknowledgeActionPermissions.canChangeNotes;
        const submittedNotes = canChangeNotes
          ? rowNotesRef.current[rowId] ?? resolveRowNotes(row)
          : undefined;
        const actionPayload = {
          acknowledgeType: normalizedType,
        };
        if (canChangeNotes) {
          actionPayload.notes = submittedNotes;
        }
        const response = await submitRtrAcknowledgeAction(rowId, actionPayload);

        if (response == null || response?.success === false) {
          toast.error(response?.message || "Unable to submit acknowledgement");
          return;
        }

        toast.success(response?.message || "Acknowledgement submitted successfully");

        const patchFromApi = extractRtrAcknowledgePatchFromApiResponse(response);
        const rowPatch = {
          ...patchFromApi,
          id: patchFromApi?.id ?? rowId,
          notes:
            patchFromApi?.notes ??
            (canChangeNotes ? submittedNotes : resolveRowNotes(row)),
          acknowledgeType: patchFromApi?.acknowledgeType ?? normalizedType,
          decisionDisplay:
            patchFromApi?.decisionDisplay ??
            buildRtrAcknowledgeDecisionDisplay(
              patchFromApi?.acknowledgeType ?? normalizedType
            ),
        };

        const activeAcknowledgeTypeFilter =
          requestPayload?.acknowledgeType ?? listFilter?.acknowledgeType ?? "";

        if (
          shouldRemoveRtrAcknowledgeRowAfterAction(
            rowPatch?.acknowledgeType ?? normalizedType,
            activeAcknowledgeTypeFilter
          )
        ) {
          removeRtrAcknowledgeRowFromList(rowId);
          return;
        }

        applyRtrAcknowledgeRowPatch(rowId, rowPatch);
        if (canChangeNotes) {
          const syncedNotes = String(rowPatch?.notes ?? submittedNotes ?? "");
          rowNotesRef.current[rowId] = syncedNotes;
          lastSavedNotesRef.current[rowId] = syncedNotes;
        }
      } catch (error) {
        toast.error(error?.message || "Unable to submit acknowledgement");
      }
    },
    [
      applyRtrAcknowledgeRowPatch,
      listFilter?.acknowledgeType,
      removeRtrAcknowledgeRowFromList,
      requestPayload?.acknowledgeType,
      rtrAcknowledgeActionPermissions.canChangeNotes,
    ]
  );

  const handleRtrAcknowledgeYes = useCallback(
    (row = {}) => submitAcknowledgeAction(row, "yes"),
    [submitAcknowledgeAction]
  );

  const handleRtrAcknowledgeNo = useCallback(
    (row = {}) => submitAcknowledgeAction(row, "no"),
    [submitAcknowledgeAction]
  );

  const handleRtrAcknowledgeCall = useCallback((row = {}) => {
    const candidateClientId = String(row?.candidateClientId ?? "").trim();
    if (!candidateClientId) {
      toast.error("Candidate client id is missing");
      return;
    }
    window.open(
      `/${urlEndPoint.callDetails}/${candidateClientId}`,
      "_blank",
      "noopener,noreferrer"
    );
  }, []);

  const handleRtrAcknowledgeDelete = useCallback(
    async (row = {}) => {
      const rowId = resolveRowId(row);
      if (!rowId) {
        toast.error("Record ID not found");
        return;
      }

      const swalResult = await Swal.fire({
        title: "Delete",
        text: "Are you sure you want to delete this acknowledgement record?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });
      if (!swalResult?.isConfirmed) return;

      try {
        const response = await deleteRtrAcknowledge(rowId);
        if (response == null || response?.success === false) {
          toast.error(response?.message);
          return;
        }
        toast.success(response?.message || "Acknowledgement deleted successfully");
        removeRtrAcknowledgeRowFromList(rowId);
      } catch (error) {
        // toast.error(error?.message || "Unable to delete acknowledgement");
      }
    },
    [removeRtrAcknowledgeRowFromList]
  );

  const filteredData = useMemo(() => tableData, [tableData]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">RTR Acknowledgement</h5>
            </div>
            <hr />

            {shouldRenderMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                remountKey={toolbarFilterRemountKey}
                toolbarDefinitions={metadataToolbarFilterDefinitions}
                listFilter={listFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarFilterSearch}
                onClear={handleFilterReset}
                showClearWhenNoModalFilters={!shouldShowTableHeaderFilterAndResetControls}
                toolbarDropdownOptionsByFieldName={toolbarDropdownReactSelectOptionsByFieldName}
                layoutPreset="callExecutive"
                pageName="RtrAcknowledge"
              />
            ) : null}

            {loading ? (
              <Loaders />
            ) : !tableMetaData?.length ? (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            ) : (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={filterApplied ? filteredData : []}
                emptyTableContent={
                  !filterApplied ? (
                    <CandidateListFilterPrompt
                      variant="table"
                      title="Apply filters to view records"
                      description="Choose your filter criteria above and click Search. Your results will appear in this table."
                      onSearch={handleUpperToolbarFilterSearch}
                    />
                  ) : undefined
                }
                isGlobalFilter={false}
                isSort={true}
                isActionRequired={true}
                isCheckboxLeftSide={false}
                enableBulkDeactivate={false}
                onRtrAcknowledgeYes={
                  rtrAcknowledgeActionPermissions.canAcknowledge
                    ? handleRtrAcknowledgeYes
                    : undefined
                }
                onRtrAcknowledgeNo={
                  rtrAcknowledgeActionPermissions.canAcknowledge
                    ? handleRtrAcknowledgeNo
                    : undefined
                }
                onRtrAcknowledgeCall={handleRtrAcknowledgeCall}
                onDelete={
                  rtrAcknowledgeActionPermissions.canDelete
                    ? handleRtrAcknowledgeDelete
                    : undefined
                }
                onRowNotesChange={
                  rtrAcknowledgeActionPermissions.canChangeNotes
                    ? handleRowNotesChange
                    : undefined
                }
                onRowNotesBlur={
                  rtrAcknowledgeActionPermissions.canChangeNotes
                    ? handleRowNotesBlur
                    : undefined
                }
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                pageName="RtrAcknowledge"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                activeFilterCount={activeModalFilterCount}
              />
            )}
          </CardBody>
        </Card>

        <AllCommonFilterModal
          setCommonFilterModalOpen={setCommonFilterModalOpen}
          commonFilterModalOpen={commonFilterModalOpen}
          modalTitle="RTR Acknowledgement Filter"
          handleSubmit={handleFilterSubmit}
          filterData={filterModalData}
          metadataFilterDefinitions={filterMetaData}
          enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
          commonResetFilter={commonResetFilter}
          setCommonResetFilter={setCommonResetFilter}
          pageName="RtrAcknowledge"
        />
      </Container>
    </div>
  );
};

RTRAcknowledge.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(RTRAcknowledge);
